<template>
  <Navbar />
  <div class="body otp-container auth-page-skin">
    <div class="otp-card auth-panel">
      <h2>{{ $t('verifyOtp.login.title') }}</h2>

      <p class="subtitle">
        {{ $t('verifyOtp.login.subtitlePrefix') }}
        {{ phoneNumber }}
        {{ $t('verifyOtp.login.subtitleSuffix') }}
      </p>

      <form @submit.prevent="handleVerifyOtp">
        <input type="hidden" v-model="otpData.phoneNumber" />

        <div v-if="errorMessage" class="text-danger error-message">
          {{ errorMessage }}
        </div>

        <div v-if="successMessage" class="text-success success-message">
          {{ successMessage }}
        </div>

        <div class="form-group">
          <label class="form-label">{{ $t('verifyOtp.login.codeLabel') }}</label>

          <input 
            v-model="otpData.otp" 
            class="form-input" 
            :placeholder="$t('verifyOtp.login.placeholder')"
            maxlength="6"
            required
          />

          <span v-if="fieldErrors.otp" class="text-danger">
            {{ fieldErrors.otp }}
          </span>
        </div>

        <button type="submit" class="verify-btn" :disabled="loading">
          {{ loading ? $t('verifyOtp.login.loading') : $t('verifyOtp.login.submit') }}
        </button>
      </form>

      <p class="resend-link">
        {{ $t('verifyOtp.login.notReceived') }}
        <a href="#" @click.prevent="handleResendOtp">
          {{ $t('verifyOtp.login.resend') }}
        </a>
      </p>
    </div>
  </div>
</template>

<script>
import Navbar from '@/components/Navbar.vue';

export default {
  name: 'VerifyOtpLogin',
  components:{
    Navbar,
  },
  data() {
    return {
      loading: false,
      errorMessage: '',
      successMessage: '',
      phoneNumber: '', 
      otpData: {
        phoneNumber: '',
        otp: ''
      },
      fieldErrors: {
        otp: ''
      }
    }
  },
  mounted() {
    // In a real app, you'd get this from Vuex store or session
    // For now, we'll try to get it from localStorage or route params
    this.phoneNumber = localStorage.getItem('loginPhoneNumber') || '09123456789';
    this.otpData.phoneNumber = this.phoneNumber;

    // Keep the page theme in sync when this view is opened directly.
    // The legacy Navbar only updates data-theme after the toggle is clicked.
    const savedTheme = localStorage.getItem('theme') || 'light';
    document.documentElement.setAttribute('data-theme', savedTheme);
    
    // Set RTL direction
    document.documentElement.setAttribute('dir', 'rtl');
    document.documentElement.setAttribute('lang', 'fa');
  },
  beforeUnmount() {
    // Cleanup
    document.documentElement.removeAttribute('dir');
    document.documentElement.removeAttribute('lang');
  },
  methods: {
    async handleVerifyOtp() {
      this.loading = true;
      this.errorMessage = '';
      this.fieldErrors.otp = '';

      try {
        // First, get the anti-forgery token
        const otpPageResponse = await fetch(`${this.$apiBaseUrl}/Customers/LoginVerifyOtp`);
        const html = await otpPageResponse.text();
        
        // Extract anti-forgery token
        const tokenMatch = html.match(/name="__RequestVerificationToken".*?value="([^"]+)"/);
        const requestVerificationToken = tokenMatch ? tokenMatch[1] : '';

        // Prepare form data
        const formData = new FormData();
        formData.append('PhoneNumber', this.otpData.phoneNumber);
        formData.append('Otp', this.otpData.otp);
        if (requestVerificationToken) {
          formData.append('__RequestVerificationToken', requestVerificationToken);
        }

        // Send OTP verification request
        const response = await fetch(`${this.$apiBaseUrl}/Customers/LoginVerifyOtp`, {
          method: 'POST',
          headers: {
            'Referer': `${this.$apiBaseUrl}/Customers/LoginVerifyOtp`,
          },
          body: formData,
          credentials: 'include' // Important for cookies/session
        });

        if (response.redirected) {
          // Successful OTP verification - redirect to dashboard
          this.$router.push('/roundtable');
        } else {
          const responseText = await response.text();
          this.parseOtpErrors(responseText);
        }
      } catch (error) {
        console.error('OTP verification error:', error);
        this.errorMessage = 'خطا در ارتباط با سرور. لطفا دوباره تلاش کنید.';
      } finally {
        this.loading = false;
      }
    },
    parseOtpErrors(html) {
      if (html.includes('Invalid or expired OTP') || html.includes('کد تأیید نامعتبر')) {
        this.fieldErrors.otp = 'کد تأیید نامعتبر یا منقضی شده است';
      } else if (html.includes('Session expired')) {
        this.errorMessage = 'سشن منقضی شده است. لطفا دوباره وارد شوید.';
        // Redirect to login after a delay
        setTimeout(() => {
          this.$router.push('/login');
        }, 2000);
      } else {
        this.fieldErrors.otp = 'کد تأیید نامعتبر است';
      }
    },
    async handleResendOtp() {
      try {
        // Implement resend OTP logic here
        // This would typically call your backend to resend the OTP
        this.successMessage = 'کد جدید ارسال شد';
        setTimeout(() => {
          this.successMessage = '';
        }, 3000);
      } catch (error) {
        this.errorMessage = 'خطا در ارسال مجدد کد';
      }
    }
  }
}
</script>

<style scoped>
.otp-container {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100vh;
  padding: 1rem;
  background-color: var(--bg);
}

/* Card */
.otp-card {
  background: var(--card-bg);
  padding: 2rem;
  border-radius: 8px;
  box-shadow: var(--shadow-2);
  text-align: center;
  width: 100%;
  max-width: 400px;
}

/* Title */
.otp-card h2 {
  font-size: 1.8rem;
  font-weight: bold;
  margin-bottom: 0.5rem;
  color: var(--heading, var(--text));
}

/* Subtitle */
.subtitle {
  font-size: 0.95rem;
  color: var(--subtext);
  margin-bottom: 1.5rem;
}

/* Form */
.form-group {
  margin-bottom: 1.5rem;
  text-align: right;
}

.form-label {
  display: block;
  text-align: right;
  font-weight: 500;
  margin-bottom: 0.5rem;
  color: var(--text);
}

/* Input */
.form-input {
  width: 100%;
  padding: 0.75rem;
  border: 1px solid var(--border);
  border-radius: 6px;
  font-size: 1rem;
  box-sizing: border-box;
  text-align: center;
  background: var(--input-bg, var(--card-bg));
  color: var(--text);
  transition: border-color 0.2s, box-shadow 0.2s;
}

.form-input:focus {
  border-color: var(--primary);
  outline: none;
  box-shadow: 0 0 0 3px var(--ring);
}

/* Button */
.verify-btn {
  width: 100%;
  background-color: var(--primary);
  color: var(--text-light, #fff);
  border: none;
  padding: 0.75rem;
  font-size: 1rem;
  font-weight: bold;
  border-radius: 6px;
  cursor: pointer;
  transition: background 0.2s;
  margin-top: 1rem;
}

.verify-btn:hover:not(:disabled) {
  background-color: var(--primary-hover);
}

.verify-btn:disabled {
  background-color: var(--disabled);
  cursor: not-allowed;
}

/* Resend link */
.resend-link {
  margin-top: 1.5rem;
  font-size: 0.9rem;
  color: var(--subtext);
}

.resend-link a {
  color: var(--primary);
  text-decoration: none;
  font-weight: 500;
}

.resend-link a:hover {
  text-decoration: underline;
}

/* Alerts */
.text-danger {
  color: var(--red);
  font-size: 0.8rem;
  margin-top: 0.25rem;
  display: block;
}

.text-success {
  color: var(--green);
  font-size: 0.8rem;
  margin-top: 0.25rem;
  display: block;
}

/* Boxes */
.error-message, 
.success-message {
  text-align: center;
  margin-bottom: 1rem;
  padding: 0.5rem;
  border-radius: 4px;
}

/* Error Box */
.error-message {
  background-color: var(--error-bg, rgba(255, 0, 0, 0.05));
  border: 1px solid var(--error-border, rgba(255, 0, 0, 0.2));
}

/* Success Box */
.success-message {
  background-color: var(--success-bg, rgba(0, 255, 0, 0.05));
  border: 1px solid var(--success-border, rgba(0, 255, 0, 0.2));
}

/* Shared Register-style auth UI */
.auth-page-skin { --auth-text:#f1f7f5; --auth-muted:#9bb5b4; --auth-surface:rgba(12,42,51,.72); --auth-border:rgba(145,205,194,.2); --auth-input:rgba(5,24,31,.72); --auth-accent:#f07832; --auth-accent-soft:#ff9b5f; position:relative; isolation:isolate; min-height:calc(100svh - 64px); overflow:hidden; padding:56px 20px 72px; color:var(--auth-text); background:radial-gradient(circle at 72% 45%,rgba(35,110,118,.25),transparent 34%),linear-gradient(135deg,#06151e 0%,#08242e 52%,#071b25 100%); font-family:'Vazirmatn',Tahoma,'Segoe UI',sans-serif; }
.auth-page-skin::before { content:''; position:absolute; z-index:-1; inset:-12%; background:url('~@/assets/register-login-bg.svg') center / min(720px,92vw) auto no-repeat; opacity:.34; pointer-events:none; }
.auth-page-skin::after { content:''; position:absolute; z-index:-1; inset:20%; border-radius:50%; background:rgba(240,120,50,.12); filter:blur(70px); pointer-events:none; }
.auth-page-skin > * { position:relative; z-index:1; }
.auth-page-skin .auth-panel { position:relative; z-index:2; width:min(100%,440px); margin:0 auto; padding:36px 32px 30px; border:1px solid var(--auth-border); border-radius:22px; background:var(--auth-surface); color:var(--auth-text); box-shadow:0 24px 70px rgba(0,0,0,.28),inset 0 1px 0 rgba(255,255,255,.04); backdrop-filter:blur(14px); }
.auth-page-skin .auth-panel h2 { color:var(--auth-text); font-size:clamp(22px,3vw,29px); font-weight:800; }
.auth-page-skin .subtitle { color:var(--auth-muted); line-height:1.8; }
.auth-page-skin .form-group { margin-bottom:18px; }
.auth-page-skin .form-label { color:var(--auth-muted); font-size:13px; font-weight:600; }
.auth-page-skin .form-input, .auth-page-skin .otp-input { width:100%; min-height:46px; padding:11px 14px; border:1px solid rgba(145,205,194,.25); border-radius:10px; outline:none; background:var(--auth-input); color:var(--auth-text); font:inherit; transition:border-color .2s,box-shadow .2s,background .2s; }
.auth-page-skin .form-input::placeholder { color:#76918f; }
.auth-page-skin .form-input:focus, .auth-page-skin .otp-input:focus { border-color:var(--auth-accent); box-shadow:0 0 0 3px rgba(240,120,50,.16); background:rgba(5,24,31,.88); }
.auth-page-skin .form-input.input-error { border-color:#e07070; }
.auth-page-skin .btn-primary, .auth-page-skin .verify-btn, .auth-page-skin .login-btn, .auth-page-skin .reset-btn, .auth-page-skin .password-btn, .auth-page-skin .submit-btn { min-height:46px; border:0; border-radius:10px; background:var(--auth-accent); color:#fff; font:inherit; font-weight:700; cursor:pointer; box-shadow:0 8px 24px rgba(240,120,50,.2); transition:transform .18s,background .18s,opacity .18s,box-shadow .18s; }
.auth-page-skin .btn-primary:hover:not(:disabled), .auth-page-skin .verify-btn:hover:not(:disabled), .auth-page-skin .login-btn:hover:not(:disabled), .auth-page-skin .reset-btn:hover:not(:disabled), .auth-page-skin .password-btn:hover:not(:disabled), .auth-page-skin .submit-btn:hover:not(:disabled) { transform:translateY(-2px); background:var(--auth-accent-soft); box-shadow:0 12px 32px rgba(240,120,50,.3); }
.auth-page-skin button:disabled { opacity:.65; cursor:wait; }
.auth-page-skin .text-danger, .auth-page-skin .error-message { color:#f28a8a; }
.auth-page-skin .text-success, .auth-page-skin .success-message { color:#7ed6a2; }
.auth-page-skin .error-message, .auth-page-skin .success-message { background:rgba(5,24,31,.48); border-color:var(--auth-border); }
.auth-page-skin .btn-secondary { background:#31545b; color:#fff; border:0; }
.auth-page-skin .btn-link, .auth-page-skin a { color:var(--auth-accent-soft); }
.auth-page-skin .role-selection-content, .auth-page-skin .company-selection-content { background:#0d2b35; color:var(--auth-text); border:1px solid var(--auth-border); }
.auth-page-skin .company-option { border-color:var(--auth-border); background:rgba(5,24,31,.38); }
@media (max-width:767px) { .auth-page-skin { min-height:calc(100svh - 56px); padding:34px 14px 40px; } .auth-page-skin::before { background-size:min(560px,125vw) auto; opacity:.28; } .auth-page-skin .auth-panel { width:min(100%,440px); padding:28px 22px; } }


/* Match Register composition: visual left, auth panel right; visual behind panel on mobile. */
.otp-container.auth-page-skin, .verify-container.auth-page-skin, .password-container.auth-page-skin, .reset-container.auth-page-skin {
  display: grid;
  grid-template-columns: minmax(0, 1.08fr) minmax(320px, .92fr);
  align-items: center;
  gap: clamp(24px, 4vw, 72px);
  width: 100%;
  min-height: calc(100svh - 64px);
  padding: 72px clamp(24px, 5vw, 88px) 32px;
}
.otp-container.auth-page-skin::before, .verify-container.auth-page-skin::before, .password-container.auth-page-skin::before, .reset-container.auth-page-skin::before {
  inset: 8% 42% 4% 2%;
  background-size: min(680px, 56vw) auto;
  background-position: center;
  opacity: .62;
}
.otp-container.auth-page-skin > .auth-panel, .verify-container.auth-page-skin > .auth-panel, .password-container.auth-page-skin > .auth-panel, .reset-container.auth-page-skin > .auth-panel {
  grid-column: 2;
  justify-self: end;
  width: min(100%, 500px);
}
.reset-container.auth-page-skin > .language-switcher { position: absolute; z-index: 3; top: 18px; inset-inline-start: 24px; }
@media (max-width: 767px) {
  .otp-container.auth-page-skin, .verify-container.auth-page-skin, .password-container.auth-page-skin, .reset-container.auth-page-skin {
    position: relative;
    display: block;
    min-height: calc(100svh - 56px);
    padding: 34px 14px 40px;
  }
  .otp-container.auth-page-skin::before, .verify-container.auth-page-skin::before, .password-container.auth-page-skin::before, .reset-container.auth-page-skin::before {
    inset: -8% -18%;
    background-size: min(560px, 125vw) auto;
    opacity: .34;
  }
  .otp-container.auth-page-skin > .auth-panel, .verify-container.auth-page-skin > .auth-panel, .password-container.auth-page-skin > .auth-panel, .reset-container.auth-page-skin > .auth-panel {
    position: relative;
    z-index: 2;
    width: min(100%, 440px);
    margin: 0 auto;
  }
}

/* RegisterCompanyPhone has an extra inner container around its panel. */
.auth-page-skin > .login-container {
  display: grid;
  grid-template-columns: minmax(0, 1.08fr) minmax(320px, .92fr);
  align-items: center;
  gap: clamp(24px, 4vw, 72px);
  min-height: calc(100svh - 64px);
  padding: 0;
  background: transparent;
}
.auth-page-skin > .login-container > .login-card {
  grid-column: 2;
  justify-self: end;
  width: min(100%, 500px);
  position: relative;
  z-index: 2;
  background: var(--auth-surface);
  border: 1px solid var(--auth-border);
  border-radius: 22px;
  color: var(--auth-text);
  box-shadow: 0 24px 70px rgba(0,0,0,.28);
  backdrop-filter: blur(14px);
}
@media (max-width: 767px) {
  .auth-page-skin > .login-container { display: block; min-height: calc(100svh - 56px); }
  .auth-page-skin > .login-container > .login-card { width: min(100%, 440px); margin: 0 auto; }
}


/* Keep auth panels translucent like Register so the visual fades through them. */
.auth-page-skin .auth-panel,
.auth-page-skin .login-card,
.register-page .form-card {
  background: transparent !important;
  backdrop-filter: blur(4px);
}

/* Center the visual directly behind the panel on phone widths. */
@media (max-width: 767px) {
  .auth-page-skin::before {
    inset: 0;
    background-position: center center;
    background-size: min(112vw, 440px) auto;
    opacity: .5;
  }

  .auth-page-skin .auth-panel,
  .auth-page-skin .login-card,
  .register-page .form-card {
    position: relative;
    z-index: 4;
    background: transparent !important;
    box-shadow: 0 18px 44px rgba(0, 0, 0, .18);
  }
}

/* Login fields must stay dark, including browser autofill. */
.register-page .form-card .form-input,
.login-page .form-card .form-input,
.login-page input.form-input {
  background: #05181f !important;
  background-color: #05181f !important;
  color: #f1f7f5 !important;
}

.register-page .form-card .form-input:-webkit-autofill,
.login-page .form-card .form-input:-webkit-autofill,
.login-page input.form-input:-webkit-autofill,
.login-page .form-input:-webkit-autofill:hover,
.login-page .form-input:-webkit-autofill:focus {
  -webkit-text-fill-color: #f1f7f5 !important;
  -webkit-box-shadow: 0 0 0 1000px #05181f inset !important;
  box-shadow: 0 0 0 1000px #05181f inset !important;
  background-color: #05181f !important;
  caret-color: #f1f7f5;
}
</style>
